import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/client';
import ReactGA from 'react-ga';
import { MetricsTracker, USER_EVENTS } from '@sm/metrics';
import { BasePage, Helmet, FourOhFourError, StaticContext } from '@sm/webassets';
import waitForUsabilla from '~app/helpers/waitForUsabilla';
import publishedApplicationListingDetails from './published-application-listing-details.graphql';
import previewApplicationListingDetails from './preview-application-listing-details.graphql';
import AppDirectoryAppDetailsSkeleton from '../Details/AppDirectoryAppDetailsSkeleton';
import AppDetails from './AppDetails';
import getCanonicalUrlOrNoIndex from '~app/helpers/getCanonicalUrlOrNoIndex';
import ModernHeader from '../../components/ModernHeader';
import AppDetailsModal from './AppDetailsModal';
import { CarouselProvider } from './components/CarouselContext';
import { useSearchParams } from '../../components/utils';

export const DetailsPage = ({
  match: {
    params: { appId },
  },
  location: { pathname },
  preview = false,
}) => {
  const {
    environment: { subdomain, domain, tld },
    url,
  } = useContext(StaticContext);
  const urlParams = useSearchParams();
  const isInModal = urlParams.get('in_modal');

  const [appDetails, setAppDetails] = useState();
  const [pageTracked, setPageTracked] = useState(false);
  const [googleTracked, setGoogleTracked] = useState(false);
  const [error, setError] = useState();
  const { noIndex, canonicalUrl } = getCanonicalUrlOrNoIndex(subdomain, domain, tld, url);

  const onQueryComplete = data => {
    const newAppDetails = preview ? data.previewApplicationListingDetails : data.publishedApplicationListingDetails;

    const { details = {}, id, name } = newAppDetails;
    if (newAppDetails && details.googleAnalyticsTrackingId && !googleTracked) {
      const trackerName = 'app-dir-details-pageview';
      ReactGA.initialize(details.googleAnalyticsTrackingId, {
        name: trackerName, // this is tracker name in case there are other trackers on the page
      });
      ReactGA.pageview(pathname, [trackerName]);
      setGoogleTracked(true);
    }

    if (newAppDetails && !pageTracked) {
      MetricsTracker.track({
        name: USER_EVENTS.COMPONENT_ADD,
        data: {
          actionFlow: 'browsing',
          actionType: 'app_details.page_view',
          appId: id,
          appName: name,
        },
      });
      setPageTracked(true);
    }
  };

  useEffect(() => {
    if (isInModal) {
      document.documentElement.style.overflow = 'hidden';
    }
  }, [isInModal]);

  const { data: queryDataResponse, refetch } = useQuery(
    preview ? previewApplicationListingDetails : publishedApplicationListingDetails,
    {
      variables: { language: 'en', appId },
      onCompleted: (response = {}) => onQueryComplete(response),
      onError: queryError => setError(queryError),
    }
  );

  useEffect(() => {
    const newAppDetails = preview
      ? queryDataResponse.previewApplicationListingDetails
      : queryDataResponse.publishedApplicationListingDetails;
    setAppDetails(newAppDetails);
  }, [queryDataResponse, preview]);

  useEffect(() => {
    waitForUsabilla(() => {
      window.usabilla_live('show');
      window.usabilla_live('setForm', 'AppDirectoryForm');
    });
  }, []);

  return (
    <BasePage pageId="App Directory - Details" includeHeader={false} includeFooter={!isInModal} legacyWeb="papi">
      <If condition={!isInModal}>
        <ModernHeader v2="true" />
      </If>
      <Choose>
        <When condition={!appDetails}>
          <AppDirectoryAppDetailsSkeleton data-testid="DetailsV2__AppDirectorySkeleton" />
        </When>
        <When condition={error}>
          <div data-testid="DetailsV2__AppDirectoryNotFound">
            <FourOhFourError />
          </div>
        </When>
        <Otherwise>
          <Helmet>
            <title>{`${appDetails.name} - SurveyMonkey App Integration`}</title>
            <meta name="description" content={appDetails.tagline} />
            <If condition={!noIndex}>
              <link rel="canonical" href={canonicalUrl} />
            </If>
            <If condition={noIndex}>
              <meta name="robots" content="noindex, nofollow" />
            </If>
          </Helmet>
          <CarouselProvider>
            <If condition={!isInModal}>
              <AppDetails appDetails={appDetails} refreshDetails={refetch} />
            </If>
            <If condition={isInModal}>
              <AppDetailsModal appDetails={appDetails} refreshDetails={refetch} />
            </If>
          </CarouselProvider>
        </Otherwise>
      </Choose>
    </BasePage>
  );
};

DetailsPage.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      appId: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
  }).isRequired,
  preview: PropTypes.bool,
};

DetailsPage.defaultProps = {
  preview: false,
};

export default DetailsPage;
